// i2p.board.types.ts
// Minimal types for Idea-to-Product Kanban board fields

/** ===== Stages ===== */
export type Stage =
  | "Ideation"
  | "Research"
  | "PoC"
  | "Marketing"
  | "Sales"
  | "Validated";

/** ===== Priority Levels ===== */
export type Priority = "low" | "medium" | "high" | "critical";

/** ===== Base Card ===== */
export interface IdeaCard {
  id: string;
  title: string;             // short name of the idea
  description?: string;      // optional quick summary
  stage: Stage;
  owner: string;             // user id / email
  priority?: Priority;
  tags?: string[];
  createdAt: string;         // ISO date
  updatedAt: string;         // ISO date
}

/** ===== Stage-Specific Fields ===== */

/* --- Ideation --- */
export interface IdeationFields {
  problem: string;           // short statement of the problem
  audience: string;          // target users/customers
  solution: string;          // short description of proposed solution
  primaryKPI?: string;       // main metric to prove success
  assumptions?: string[];    // list of assumptions to validate
}

/* --- Research --- */
export interface ResearchFields {
  hypotheses?: string[];     // statements to test
  successCriteria?: string;  // threshold for success
  competitors?: string[];    // main competitors
  marketNotes?: string;      // quick market insight
  evidence?: string;         // summary of collected evidence
}

/* --- PoC --- */
export interface PoCFields {
  pocScope?: string;         // what the PoC will include
  criticalFlow?: string;     // main user journey tested
  acceptanceCriteria?: string; // what defines PoC success
  notes?: string;            // any other relevant notes
}

/* --- Marketing --- */
export interface MarketingFields {
  positioning?: string;      // problem → solution → proof → CTA
  primaryCTA?: string;       // e.g., "join waitlist"
  targetSegments?: string[]; // audience breakdown
}

/* --- Sales --- */
export interface SalesFields {
  icp?: string;              // ideal customer profile
  offer?: string;            // what's being pitched
  successMetric?: string;    // meeting count, pilot deals, etc.
}

/* --- Validated --- */
export interface ValidatedFields {
  metricsSummary?: string;   // quick KPI summary
  decision?: "proceed" | "iterate" | "stop";
  rationale?: string;        // reason for decision
}

/** ===== Full Board Card (with all possible stage fields) ===== */
export interface BoardCard
  extends IdeaCard,
    Partial<IdeationFields>,
    Partial<ResearchFields>,
    Partial<PoCFields>,
    Partial<MarketingFields>,
    Partial<SalesFields>,
    Partial<ValidatedFields> {}

/** ===== Stage Configuration ===== */
export interface StageConfig {
  name: Stage;
  color: string;
  requiredFields: string[];
  optionalFields: string[];
}

/** Type alias for stage configurations map */
export type StageConfigsMap = Record<Stage, StageConfig>;

export const STAGE_CONFIGS: StageConfigsMap = {
  Ideation: {
    name: "Ideation",
    color: "#e3f2fd",
    requiredFields: ["problem", "audience", "solution"],
    optionalFields: ["primaryKPI", "assumptions"]
  },
  Research: {
    name: "Research",
    color: "#fff3e0",
    requiredFields: [],
    optionalFields: ["hypotheses", "successCriteria", "competitors", "marketNotes", "evidence"]
  },
  PoC: {
    name: "PoC",
    color: "#f3e5f5",
    requiredFields: [],
    optionalFields: ["pocScope", "criticalFlow", "acceptanceCriteria", "notes"]
  },
  Marketing: {
    name: "Marketing",
    color: "#e8f5e8",
    requiredFields: [],
    optionalFields: ["positioning", "primaryCTA", "targetSegments"]
  },
  Sales: {
    name: "Sales",
    color: "#fff8e1",
    requiredFields: [],
    optionalFields: ["icp", "offer", "successMetric"]
  },
  Validated: {
    name: "Validated",
    color: "#f1f8e9",
    requiredFields: [],
    optionalFields: ["metricsSummary", "decision", "rationale"]
  }
};
